// Admin-only CFO-Controlling-Export.
//
// Reiner Lesezugriff: erzeugt aus den bereits geladenen Daten mehrere CSV-Dateien
// (+ README) für Effizienz-, Margen- und Auslastungsanalyse — einzeln oder als ZIP.
// Keine Mutationen, keine zusätzlichen Abfragen, keine Auth-/Rechte-Änderung.
import { useState, useMemo } from 'react';
import { useData } from './DataContext.jsx';
import { durationMin, todayIso, pad } from './lib/format.js';
import {
  buildAllFiles, downloadFile, downloadZip, exportStamp,
} from './lib/cfo-export.js';
import { IconDownload, IconClock, IconFolder, IconUsers, IconCheck } from './icons.jsx';

// Liefert { from, to } (ISO, inklusiv) für die gewählte Voreinstellung.
function rangeForPreset(preset, custom) {
  const today = new Date();
  const to = todayIso(today);
  if (preset === 'all') return { from: null, to: null };
  if (preset === 'custom') {
    return { from: custom.from || null, to: custom.to || null };
  }
  const months = preset === '12m' ? 12 : 18;
  const from = new Date(today);
  from.setMonth(from.getMonth() - months);
  return { from: `${from.getFullYear()}-${pad(from.getMonth() + 1)}-${pad(from.getDate())}`, to };
}

const PRESETS = [
  { key: '18m', label: 'Letzte 18 Monate' },
  { key: '12m', label: 'Letzte 12 Monate' },
  { key: 'all', label: 'Gesamter Zeitraum' },
  { key: 'custom', label: 'Benutzerdefiniert' },
];

const FILE_META = [
  { name: 'time_entries.csv', desc: 'Eine Zeile pro Zeiteintrag — Kern für Stundensatz & verrechenbare Zeit', Icon: IconClock },
  { name: 'projects.csv', desc: 'Projekte/Mandate inkl. Budget, Status, Abrechnungs-Flags', Icon: IconFolder },
  { name: 'employees.csv', desc: 'Mitarbeitende, Pensum & Verrechnungssatz', Icon: IconUsers },
  { name: 'tasks.csv', desc: 'Aufgaben je Projekt (Kapazitätsplanung)', Icon: IconCheck },
  { name: 'utilization_monthly.csv', desc: 'Verdichtung: Ist-/Soll-Stunden pro Person & Monat', Icon: IconClock },
  { name: 'README_export.txt', desc: 'Format, Join-Schlüssel, fehlende Felder', Icon: IconFolder },
];

function Stat({ label, value, sub }) {
  return (
    <div className="card card-pad" style={{ flex: 1, minWidth: 0 }}>
      <div className="eyebrow">{label}</div>
      <div className="serif" style={{ fontSize: 24, fontWeight: 500, marginTop: 6 }}>{value}</div>
      {sub && <div className="tiny muted" style={{ marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export function CfoExportPage() {
  const data = useData();
  const { isAdmin, timeEntries, projects, profiles, tasks, customers, absences } = data;
  const [preset, setPreset] = useState('18m');
  const [custom, setCustom] = useState({ from: '', to: todayIso() });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // letzter Download-Hinweis

  const range = useMemo(() => rangeForPreset(preset, custom), [preset, custom]);

  // Vorschau-Kennzahlen für den gewählten Zeitraum (rein lesend).
  const preview = useMemo(() => {
    const inRange = timeEntries.filter(te => {
      const d = te.entry_date;
      if (!d) return false;
      if (range.from && d < range.from) return false;
      if (range.to && d > range.to) return false;
      return true;
    });
    const totalMin = inRange.reduce((s, te) => s + durationMin(te.from_time, te.to_time), 0);
    const billableMin = inRange.reduce((s, te) => s + (te.billable ? durationMin(te.from_time, te.to_time) : 0), 0);
    return {
      entries: inRange.length,
      hours: (totalMin / 60),
      billableHours: (billableMin / 60),
      billablePct: totalMin > 0 ? Math.round((billableMin / totalMin) * 100) : 0,
    };
  }, [timeEntries, range]);

  if (!isAdmin) {
    return (
      <div className="content">
        <div className="card card-pad" style={{ maxWidth: 480 }}>
          <div className="eyebrow">CFO-Export</div>
          <div style={{ marginTop: 8 }}>Dieser Bereich ist nur für Admins zugänglich.</div>
        </div>
      </div>
    );
  }

  const dataBundle = { timeEntries, projects, profiles, tasks, customers, absences };

  function buildFiles() {
    return buildAllFiles(dataBundle, range);
  }

  function handleZip() {
    setBusy(true);
    try {
      const files = buildFiles();
      downloadZip(`cfo-export_${exportStamp()}.zip`, files);
      setDone('ZIP mit allen Dateien heruntergeladen.');
    } finally {
      setBusy(false);
    }
  }

  function handleSingle(name) {
    const files = buildFiles();
    const f = files.find(x => x.name === name);
    if (f) {
      downloadFile(f.name, f.text);
      setDone(`${name} heruntergeladen.`);
    }
  }

  const customInvalid = preset === 'custom' && custom.from && custom.to && custom.from > custom.to;

  return (
    <div className="content">
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow">Controlling</div>
        <h1 className="serif" style={{ fontSize: 30, fontWeight: 500, margin: '4px 0 6px' }}>CFO-Export</h1>
        <div className="muted" style={{ fontSize: 14, maxWidth: 640 }}>
          Read-only Datenexport der Zeitseite für Effizienz-, Margen- und Auslastungsanalyse.
          CSV (UTF-8 mit BOM, Semikolon, Dezimalpunkt, ISO-Datum) — sauber joinbar per IDs.
        </div>
      </div>

      {/* Zeitraum-Filter */}
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>Zeitraum</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: preset === 'custom' ? 12 : 0 }}>
          {PRESETS.map(p => (
            <button
              key={p.key}
              className={`btn ${preset === p.key ? 'btn-primary' : ''}`}
              onClick={() => setPreset(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="tiny muted">Von</span>
              <input type="date" value={custom.from}
                onChange={e => setCustom(c => ({ ...c, from: e.target.value }))}
                className="input" style={{ colorScheme: 'dark' }}/>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="tiny muted">Bis</span>
              <input type="date" value={custom.to}
                onChange={e => setCustom(c => ({ ...c, to: e.target.value }))}
                className="input" style={{ colorScheme: 'dark' }}/>
            </label>
            {customInvalid && <span className="tiny" style={{ color: 'var(--crit)' }}>„Von" liegt nach „Bis".</span>}
          </div>
        )}
      </div>

      {/* Vorschau-Kennzahlen */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="Zeiteinträge" value={preview.entries.toLocaleString('de-DE')} sub="im gewählten Zeitraum"/>
        <Stat label="Stunden gesamt" value={preview.hours.toFixed(1)} sub="alle Tätigkeitsarten"/>
        <Stat label="davon verrechenbar" value={preview.billableHours.toFixed(1)} sub={`${preview.billablePct}% billable`}/>
        <Stat label="Projekte / Mitarbeitende" value={`${projects.length} / ${profiles.length}`} sub="gesamt (inkl. archiviert)"/>
      </div>

      {/* Haupt-Download */}
      <div className="card card-pad" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>Alles als ZIP</div>
          <div className="tiny muted">Alle 5 CSVs + README in einem Download.</div>
        </div>
        <button className="btn btn-primary" disabled={busy || customInvalid} onClick={handleZip}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <IconDownload size={14}/> {busy ? 'Erzeuge…' : 'ZIP herunterladen'}
        </button>
      </div>

      {/* Einzeldateien */}
      <div className="card card-pad">
        <div className="eyebrow" style={{ marginBottom: 12 }}>Einzelne Dateien</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {FILE_META.map(({ name, desc, Icon }) => (
            <div key={name} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px',
              borderTop: '1px solid var(--stroke)',
            }}>
              <div style={{ color: 'var(--ink-3)' }}><Icon size={16}/></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{name}</div>
                <div className="tiny muted">{desc}</div>
              </div>
              <button className="btn" disabled={customInvalid} onClick={() => handleSingle(name)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <IconDownload size={12}/> CSV
              </button>
            </div>
          ))}
        </div>
      </div>

      {done && (
        <div className="tiny" style={{ marginTop: 12, color: 'var(--ok)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconCheck size={12}/> {done}
        </div>
      )}
    </div>
  );
}
